import { Component, inject, InjectionToken, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { ReactiveFormsModule } from '@angular/forms';

import { RouterLink, RouterLinkActive } from '@angular/router';
import { BaseApiService } from '@shared/services/apiService/base-api.service';
import { GroupsApiDTO } from '../groups/groups.dto';
import { FarmsApiDTO } from '../farms/farms.dto';
import { ActionsApiDTO } from '../actions/actions.dto';
import { AuthStoreService } from '@features/auth/infrastructure/services/auth-store/auth-store.service';
import { UserDataApiDTO } from '../users/user.dto';
import { TranslocoDirective } from '@jsverse/transloco';
import { ScheduledPlansApiDto } from '../scheduler/scheduled-plans/scheduled-plans.dto';
import { LotsApiDTO } from '../lots/lots.dto';
import { takeUntil } from 'rxjs';
import { UnsubscribeService } from '@shared/services/unsubscribe.service';
import { documents_token, DocumentsApiDTO } from '../documents/documents.dto';
import { cows_token, CowsApiDTO } from '../cows/cows.dto';
import { RolePermissionService } from '@shared/services/role-permission.service';

const groups_apiServiceToken =
  new InjectionToken<BaseApiService<GroupsApiDTO>>('groupsApiService');
const farms_apiServiceToken =
  new InjectionToken<BaseApiService<FarmsApiDTO>>('groupsApiService');
const actions_apiServiceToken =
  new InjectionToken<BaseApiService<ActionsApiDTO>>('actionsApiService');
const users_apiServiceToken =
  new InjectionToken<BaseApiService<UserDataApiDTO>>('usersApiService');
const scheduledPlans_apiServiceToken =
  new InjectionToken<BaseApiService<ScheduledPlansApiDto>>('scheduledPlansApiService');
const lots_apiServiceToken =
  new InjectionToken<BaseApiService<LotsApiDTO>>('lotsApiService');

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  styles: [],
  hostDirectives: [],
  standalone: true,
  providers: [
    UnsubscribeService,
    {
      provide: groups_apiServiceToken,
      useFactory: () => new BaseApiService<GroupsApiDTO>('groups'),
    },
    {
      provide: farms_apiServiceToken,
      useFactory: () => new BaseApiService<FarmsApiDTO>('farms'),
    },
    {
      provide: actions_apiServiceToken,
      useFactory: () => new BaseApiService<FarmsApiDTO>('actions'),
    },
    {
      provide: users_apiServiceToken,
      useFactory: () => new BaseApiService<UserDataApiDTO>('userData'),
    },
    {
      provide: scheduledPlans_apiServiceToken,
      useFactory: () => new BaseApiService<ScheduledPlansApiDto>('scheduledPlans'),
    },
    {
      provide: lots_apiServiceToken,
      useFactory: () => new BaseApiService<LotsApiDTO>('lots'),
    },
    {
      provide: documents_token,
      useFactory: () => new BaseApiService<DocumentsApiDTO>('documents'),
    },
    {
      provide: cows_token,
      useFactory: () => new BaseApiService<CowsApiDTO>('cows'),
    },
  ],
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RouterLinkActive,
    RouterLink,
    NgbCollapseModule,
    TranslocoDirective,
  ],
})
export class SidebarComponent implements OnInit {
  @Input() sidebarClasses?: string;
  farmsIsCollapsed: boolean = true;
  livestockIsCollapsed: boolean = true;
  fieldsIsCollapsed: boolean = true;
  productionIsCollapsed: boolean = true;
  userIsCollapsed: boolean = true;
  counterMap = new Map<string, number>();
  private farms_apiService = inject(farms_apiServiceToken);
  private groups_apiService = inject(groups_apiServiceToken);
  private actions_apiService = inject(actions_apiServiceToken);
  private users_apiService = inject(users_apiServiceToken);
  private scheduledPlans_apiService = inject(scheduledPlans_apiServiceToken);
  private lots_apiService = inject(lots_apiServiceToken);
  private documents_apiService = inject(documents_token);
  private cows_apiService = inject(cows_token);
  private authStore = inject(AuthStoreService);
  user = this.authStore.user();
  private unsub = inject(UnsubscribeService);
  #rolePermissionService = inject(RolePermissionService);

  ngOnInit(): void {
    this.farms_apiService.count().pipe(takeUntil(this.unsub.$unsub)).subscribe(count => {
      this.counterMap.set('farms', count ?? 0);
    });
    this.groups_apiService.count().pipe(takeUntil(this.unsub.$unsub)).subscribe(count => {
      this.counterMap.set('groups', count ?? 0);
    });
    this.actions_apiService.count().pipe(takeUntil(this.unsub.$unsub)).subscribe(count => {
      this.counterMap.set('actions', count ?? 0);
    });
    this.scheduledPlans_apiService.count().pipe(takeUntil(this.unsub.$unsub)).subscribe(count => {
      this.counterMap.set('scheduledPlans', count ?? 0);
    });
    this.lots_apiService.count().pipe(takeUntil(this.unsub.$unsub)).subscribe(count => {
      this.counterMap.set('lots', count ?? 0);
    });
    this.documents_apiService.count().pipe(takeUntil(this.unsub.$unsub)).subscribe(count => {
      this.counterMap.set('documents', count ?? 0);
    });
    this.cows_apiService.count().pipe(takeUntil(this.unsub.$unsub)).subscribe(count => {
      this.counterMap.set('cows', count ?? 0);
    });
    this.users_apiService.count({
        where: {
          role_id: {
            equals: 3, // 3 - GUEST
          },
        },
      },
    ).pipe(takeUntil(this.unsub.$unsub)).subscribe(count => {
      this.counterMap.set('approve-users', count ?? 0);
    });
  }

  checkPermission(r: string) {
    return this.#rolePermissionService.isRoutePermitted(r);
  }
}
